import React from 'react';
import { isKioskDineInOrderCode, isQuickChargeOrderCode } from 'shared-models/browser';
import type { ReceiptPaymentView, ReceiptView } from 'shared-models/browser';
import { formatMoney as formatPosMoney, isolateAmountLtr } from '../../../../../utils/money';
import { creditMethodLabel } from '../../../../../utils/credit-method-label';
import { formatOrderDate, formatPrintDate, getOrderNumberDisplay } from '../shared/receipt.utils';
import { formatTipWithPercent } from '../../orders/tip/tip.utils';
import classes from './transaction-receipt.module.scss';

const DashedSeparator: React.FC = () => (
  <div className={classes.dashedSeparatorContainer} aria-hidden="true">
    <div className={classes.dashedLine} />
  </div>
);

// Cash, or the credit acquisition method: manual / Tap-on-Phone (device) / pinpad terminal name.
export const paymentMethodLabel = (payment: ReceiptPaymentView): string =>
  creditMethodLabel(payment.method, payment.creditTransaction?.source, payment.terminalName);

const CardRows: React.FC<{ payment: ReceiptPaymentView; roundAmounts: boolean }> = ({ payment, roundAmounts }) => {
  const txn = payment.creditTransaction!;
  const rows: Array<[string, string | null]> = [
    ['מספר עסקה', txn.referenceNumber],
    ['סוג כרטיס', txn.cardBrand],
    ['מספר כרטיס', txn.cardLast4],
    ['סכום עסקה', isolateAmountLtr(formatPosMoney(payment.amount < 0 ? -txn.amount : txn.amount, roundAmounts))],
  ];
  const visible = rows.filter(([, value]) => value != null && value !== '');
  if (visible.length === 0) {
    return null;
  }
  return (
    <div className={classes.cardRows}>
      {visible.map(([label, value]) => (
        <div key={label} className={classes.cardRow}>{label}: {value}</div>
      ))}
    </div>
  );
};

// Summary: Pure paper renderer for the post-payment קבלה (items, totals, payments, card rows).
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-receipt
export const TransactionReceiptContent: React.FC<{ receipt: ReceiptView; title?: string; showPaymentDetails?: boolean }> = ({
  receipt,
  title,
  showPaymentDetails = true,
}) => {
  const formatMoney = (value: number) => formatPosMoney(value, receipt.roundAmounts ?? false);
  const isRefundReceipt = receipt.payments.some((payment) => payment.amount < 0)
    || receipt.totals.billableTotal < 0
    || receipt.totals.paidAmount < 0;
  const isRefundOrder = receipt.orderCode.endsWith(' זיכוי');
  const resolvedTitle = title ?? (isRefundReceipt ? 'חשבונית מס זיכוי' : 'חשבונית מס/קבלה');
  const totalLabel = resolvedTitle === 'חשבון' ? 'סה"כ הזמנה' : 'סה״כ לתשלום';
  const {
    discountAmount: orderDiscount,
    billableTotal: billable,
    vatRatePercent,
    exVatTotal,
    vatAmount,
    tipAmount,
    paidAmount,
    changeAmount,
  } = receipt.totals;
  // VAT split is backend-computed from the tenant's current rate; 0% hides the split rows.
  const showVatSplit = vatRatePercent > 0;
  const isBillView = resolvedTitle === 'חשבון';
  const waiterName = receipt.isPartial
    ? (receipt.payments[0]?.paidByName ?? '--')
    : (receipt.openedByName ?? '--');
  // Quick-charge orders are take_away with a QC_ code; show "חיוב/זיכוי מהיר" as their type label.
  const isQuickCharge = receipt.orderType === 'take_away' && isQuickChargeOrderCode(receipt.orderCode);
  const takeAwayLabel = isQuickCharge ? 'חיוב/זיכוי מהיר' : 'לקחת';

  return (
    <>
      <div className={classes.headerText}>
        {receipt.header.split('\n').map((line, index) => (
          <span key={index}>{line}</span>
        ))}
      </div>

      <div className={classes.metaBlock}>
        <div className={classes.metaCenteredRow}>הודפס בתאריך: {formatPrintDate()}</div>
        <div className={classes.metaCenteredRow}>תאריך תחילת הזמנה: {formatOrderDate(receipt.createdAt)}</div>
      </div>

      <div className={classes.orderPropsGrid}>
        <div className={classes.orderPropsRow}>
          <span>מספר הזמנה {getOrderNumberDisplay(receipt.orderCode, receipt.orderId)}</span>
          {(receipt.orderType === 'take_away' || receipt.tableNumber || !isRefundOrder) && <span>{receipt.orderType === 'take_away' ? takeAwayLabel : isKioskDineInOrderCode(receipt.orderCode) ? 'לשבת' : `שולחן: ${receipt.tableNumber ?? receipt.orderCode}`}</span>}
        </div>
        <div className={classes.orderPropsRow}>
          <span>מלצר: {waiterName}</span>
          <span>סועדים: {receipt.dinersCount}</span>
        </div>
        {receipt.guestName && (
          <div className={classes.orderPropsCentered}>
            <span>שם לקוח: {receipt.guestName}</span>
          </div>
        )}
      </div>

      <DashedSeparator />
      <div className={classes.receiptTitle}>{resolvedTitle}</div>

      {receipt.isPartial ? (
        <>
          <div className={classes.calcBlock}>
            <div className={classes.totalRowBold}>
              <span>ארוחה עסקית</span>
              <span>{isolateAmountLtr(formatMoney(billable))}</span>
            </div>
            {showVatSplit && (
              <>
                <div className={classes.calcRow}>
                  <span>חייב במע"מ</span>
                  <span>{isolateAmountLtr(formatMoney(exVatTotal))}</span>
                </div>
                <div className={classes.calcRow}>
                  <span>מע״מ {vatRatePercent}%</span>
                  <span>{isolateAmountLtr(formatMoney(vatAmount))}</span>
                </div>
              </>
            )}
            {tipAmount !== 0 && (
              <div className={classes.totalRowNormal}>
                <span>טיפ</span>
                <span>{formatMoney(tipAmount)}</span>
              </div>
            )}
            <div className={classes.totalRowBold}>
              <span>סה״כ</span>
              <span>{isolateAmountLtr(formatMoney(paidAmount))}</span>
            </div>
          </div>

          <DashedSeparator />
        </>
      ) : (
        <>
          {receipt.lines.length === 0 ? (
            <div className={classes.emptyItems}>אין פריטים בהזמנה.</div>
          ) : (
            <ul className={classes.itemsList}>
              {receipt.lines.map((line) => (
                <React.Fragment key={line.orderlineId}>
                  <li className={`${classes.itemRow}${line.isCanceled ? ` ${classes.canceledItemRow}` : ''}`}>
                    <div className={classes.itemRight}>
                      <span className={classes.itemQty}>{line.quantity}</span>
                      <span className={classes.itemName}>
                        {line.variantLabels.length > 0
                          ? `${line.name} (${line.variantLabels.join(', ')})`
                          : line.name}
                        {line.isCanceled && <span className={classes.canceledTag}>מבוטל</span>}
                      </span>
                    </div>
                    <span className={classes.itemPrice}>{isolateAmountLtr(formatMoney(line.lineTotal - line.discountAmount))}</span>
                  </li>
                  {line.comboItems.map((item, index) => (
                    <li key={`${line.orderlineId}-sub-${index}`} className={`${classes.itemRow} ${classes.itemSubRow}`}>
                      <div className={classes.itemRight}>
                        <span className={classes.itemQty} />
                        <span className={classes.itemName}>{item.name}</span>
                      </div>
                      <span className={classes.itemPrice}>+{formatMoney(item.extraPrice)}</span>
                    </li>
                  ))}
                </React.Fragment>
              ))}
              {orderDiscount > 0 && (
                <li className={classes.itemRow}>
                  <div className={classes.itemRight}>
                    <span className={classes.itemQty} />
                    <span className={classes.itemName}>הנחה</span>
                  </div>
                  <span className={classes.itemPrice}>{isolateAmountLtr(`-${formatMoney(orderDiscount)}`)}</span>
                </li>
              )}
            </ul>
          )}

          <DashedSeparator />

          {showVatSplit && !isBillView && (
            <>
              <div className={classes.calcBlock}>
                <div className={classes.calcRow}>
                  <span>חייב במע"מ</span>
                  <span>{isolateAmountLtr(formatMoney(exVatTotal))}</span>
                </div>
                <div className={classes.calcRow}>
                  <span>מע״מ {vatRatePercent}%</span>
                  <span>{isolateAmountLtr(formatMoney(vatAmount))}</span>
                </div>
                <div className={classes.calcRow}>
                  <span>סה"כ כולל מע"מ</span>
                  <span>{isolateAmountLtr(formatMoney(billable))}</span>  
                </div>
              </div>

              <DashedSeparator />
            </>
          )}

          <div className={classes.totalsBlock}>
            <div className={classes.totalRowBold}>
              <span>{totalLabel}</span>
              <span>{isolateAmountLtr(formatMoney(billable))}</span>
            </div>
            {tipAmount !== 0 && (
              <div className={classes.totalRowNormal}>
                <span>טיפ</span>
                <span dir="ltr">{formatTipWithPercent(tipAmount, billable, receipt.roundAmounts ?? false)}</span>
              </div>
            )}
            <div className={classes.totalRowNormal}>
              <span>{changeAmount > 0 ? 'סה״כ התקבל' : 'סה״כ שולם'}</span>
              <span>{isolateAmountLtr(formatMoney(paidAmount))}</span>
            </div>
            {changeAmount > 0 && (
              <div className={classes.totalRowNormal}>
                <span>עודף</span>
                <span>{formatMoney(changeAmount)}</span>
              </div>
            )}
          </div>

          <DashedSeparator />
        </>
      )}

      {showPaymentDetails && (
        <div className={classes.paymentsBlock}>
          {receipt.payments.map((payment) => (
            <div key={payment.paymentlineId} className={classes.paymentItem}>
              <div className={`${classes.paymentRow}${receipt.isPartial ? ` ${classes.paymentRowCentered}` : ''}`}>
                <span>{paymentMethodLabel(payment)}</span>
                {!receipt.isPartial && <span>{isolateAmountLtr(formatMoney(payment.amount))}</span>}
              </div>
              {payment.creditTransaction && <CardRows payment={payment} roundAmounts={receipt.roundAmounts ?? false} />}
            </div>
          ))}
        </div>
      )}

      <div className={classes.deliveryTypeRow}>
        <span>{receipt.orderType === 'take_away' ? takeAwayLabel : 'לשבת'}</span>
      </div>

      <div className={classes.poweredBy}>Powered by CommunicateAI</div>
    </>
  );
};
